import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { usePropertyStore, useSelectedPropertyType } from '../store/propertyStore'
import { verticalsOwning } from '../constants/navigation'

/**
 * Keeps property-scoped pages and the selected property in agreement.
 *
 * Wraps the routes that need a property. A page belongs to a vertical when that
 * vertical's sidebar nav lists it, so switching from a security property to a
 * restaurant one takes Transcripts away, while switching between two security
 * properties leaves you where you are — same nav, same valid path.
 *
 * This is a coherence guard, not an access control boundary. Every page below
 * fetches with the selected property's id, so a wrong-vertical page shows the
 * user's own data, just an irrelevant view of it. Tenant isolation is the API's
 * job and must not be assumed from this component.
 */
export function PropertyGuard(): JSX.Element {
  const { pathname } = useLocation()
  const propertyType = useSelectedPropertyType()
  const initialized = usePropertyStore((s) => s.initialized)
  const hasNoProperties = usePropertyStore((s) => s.initialized && s.properties.length === 0)

  // Before the first fetch settles, a null type means "not known yet". Judging
  // now would bounce anyone who reloads the page they were already on.
  if (!initialized) return <Outlet />

  // "/" is the one place that decides where a user belongs — send them there
  // rather than naming a destination here.
  if (!propertyType) {
    const owners = verticalsOwning(pathname)
    if (hasNoProperties && owners.length === 0) return <Outlet />
    return <Navigate to="/" replace />
  }

  const owners = verticalsOwning(pathname)
  // Owned by no vertical means every property type has it (/admin) — it needed
  // a property, and there is one.
  if (owners.length === 0) return <Outlet />

  return owners.includes(propertyType) ? <Outlet /> : <Navigate to="/" replace />
}
